namespace $ {

	export class $shm_wordstat_data extends $mol_object {
		static attach_promises = new Map< string, Promise< void > >()
		connection_promise: Promise< any > | null = null

		app_id() { return 'wordstat' }
		file_name() { return '' }
		file_source(): $shm_wordstat_task_file | null { return null }
		queries(): readonly string[] { return [] }
		regions(): readonly string[] { return [] }
		periods(): readonly string[] { return [] }
		devices(): readonly string[] { return [] }
		date_from() { return '0001-01-01' }
		date_to() { return '2999-12-31' }

		sql_text( value: unknown ) {
			return "'" + String( value ).replaceAll( "'", "''" ) + "'"
		}

		sql_ident( value: string ) {
			return '"' + String( value ).replaceAll( '"', '""' ) + '"'
		}

		alias( file_name = this.file_name() ) {
			let hash = 2166136261
			for( const char of file_name ) hash = Math.imul( hash ^ char.charCodeAt( 0 ), 16777619 )
			return `wordstat_source_${ this.app_id().replaceAll( /[^a-zA-Z0-9_]/g, '_' ) }_${ ( hash >>> 0 ).toString( 36 ) }`
		}

		registered_name() {
			return `wordstat_${ this.app_id().replaceAll( /[^a-zA-Z0-9_]/g, '_' ) }.duckdb`
		}

		@ $mol_mem
		connection() {
			return $mol_wire_sync( this ).connection_async()
		}

		connection_async() {
			return this.connection_promise ??= $shm_duckdb.connection()
		}

		table_ready() {
			return this.table_ready_by_name( this.file_name() )
		}

		@ $mol_mem_key
		table_ready_by_name( file_name: string ) {
			return $mol_wire_sync( this ).table_ready_async( file_name )
		}

		async table_ready_async( file_name: string ) {
			const source = this.file_source()
			const buffer = source ? await $mol_wire_async( source ).buffer() : null
			const conn = await this.connection_async()
			const alias = this.alias( file_name )
			if( !buffer ) {
				await conn.query( `
					CREATE OR REPLACE TEMP TABLE wordstat_rows_empty AS
					SELECT
						''::VARCHAR AS "query", ''::VARCHAR AS "region",
						''::VARCHAR AS "period", ''::VARCHAR AS "device",
						NULL::DATE AS "date", 0::BIGINT AS "count", ''::VARCHAR AS "share"
					WHERE FALSE;
				` )
				return 'wordstat_rows_empty'
			}

			const table = `${ this.sql_ident( alias ) }.main.${ this.sql_ident( 'wordstat_rows' ) }`
			let attach = $shm_wordstat_data.attach_promises.get( alias )
			if( !attach ) {
				attach = ( async()=> {
					const db = await $shm_duckdb.db_async()
					const filename = file_name || this.registered_name()
					try { await db.dropFile( filename ) } catch( _ ) {}
					await db.registerFileBuffer( filename, new Uint8Array( buffer.slice( 0 ) ) )
					await conn.query( `ATTACH ${ this.sql_text( filename ) } AS ${ this.sql_ident( alias ) } (READ_ONLY);` )
				} )()
				$shm_wordstat_data.attach_promises.set( alias, attach )
			}
			try {
				await attach
			} catch( error ) {
				if( $shm_wordstat_data.attach_promises.get( alias ) === attach ) {
					$shm_wordstat_data.attach_promises.delete( alias )
				}
				throw error
			}
			return table
		}

		list_sql( values: readonly string[] ) {
			return values.length ? values.map( value => this.sql_text( value ) ).join( ', ' ) : `''`
		}

		period_end_sql() {
			return `CASE
					WHEN "period" = 'PERIOD_MONTHLY' THEN last_day( "date" )
					WHEN "period" = 'PERIOD_WEEKLY' THEN CAST( "date" + INTERVAL 6 DAY AS DATE )
					ELSE "date"
				END`
		}

		@ $mol_mem_key
		period_available( period: string ) {
			const table = this.table_ready()
			const result = this.query( `
				SELECT
					CAST( MIN( "date" ) AS VARCHAR ) AS "from",
					CAST( MAX( ${ this.period_end_sql() } ) AS VARCHAR ) AS "to"
				FROM ${ table }
				WHERE "period" = ${ this.sql_text( period ) };
			` )
			const row = result.rows[0]
			const from = String( row?.[0] || '' ).slice( 0, 10 )
			const to = String( row?.[1] || '' ).slice( 0, 10 )
			return from && to ? { from, to } : null
		}

		@ $mol_mem
		facets() {
			const table = this.table_ready()
			const values = ( column: string )=> {
				const ident = this.sql_ident( column )
				return this.query( `
					SELECT DISTINCT ${ ident }
					FROM ${ table }
					WHERE ${ ident } IS NOT NULL AND ${ ident } != ''
					ORDER BY ${ ident };
				` ).rows.map( row => String( row[0] ) )
			}
			return {
				queries: values( 'query' ),
				regions: values( 'region' ),
				periods: values( 'period' ),
				devices: values( 'device' ),
			}
		}

		where_sql() {
			return [
				`"query" IN (${ this.list_sql( this.queries() ) })`,
				`"region" IN (${ this.list_sql( this.regions() ) })`,
				`"period" IN (${ this.list_sql( this.periods() ) })`,
				`"device" IN (${ this.list_sql( this.devices() ) })`,
				`${ this.period_end_sql() } BETWEEN DATE ${ this.sql_text( this.date_from() ) } AND DATE ${ this.sql_text( this.date_to() ) }`,
			].join( '\n\t\t\t\t\tAND ' )
		}

		@ $mol_mem_key
		query( sql: string ): $shm_wordstat_data_table {
			const table = $mol_wire_sync( this.connection() ).query( sql )
			return $shm_duckdb.get_head_and_rows_from_vectors( table )
		}

		@ $mol_mem
		query_table() {
			const table = this.table_ready()
			return this.query( `
				SELECT
					"query" AS "Запрос",
					SUM( "count" ) AS "Кол-во",
					"region" AS "Регион",
					CAST( MIN( "date" ) AS VARCHAR ) AS "С",
					CAST( MAX( ${ this.period_end_sql() } ) AS VARCHAR ) AS "По",
					"period" AS "Периодичность",
					"device" AS "Устройство",
					"region" AS "Wordstat"
				FROM ${ table }
				WHERE ${ this.where_sql() }
				GROUP BY "query", "region", "period", "device"
				ORDER BY "Кол-во" DESC, "query", "region", "period", "device";
			` )
		}

		@ $mol_mem
		query_summary() {
			const table = this.table_ready()
			return this.query( `
				SELECT COUNT( DISTINCT "query" ) AS "Запросов", COUNT(*) AS "Строк", SUM( "count" ) AS "Показов"
				FROM ${ table }
				WHERE ${ this.where_sql() };
			` )
		}

		@ $mol_mem
		chart_timeseries() {
			const table = this.table_ready()
			const result = this.query( `
				SELECT CAST( "date" AS VARCHAR ), "query", "region", "device", SUM( "count" )
				FROM ${ table }
				WHERE ${ this.where_sql() }
				GROUP BY 1, 2, 3, 4
				ORDER BY 1, 2, 3, 4;
			` )
			return result.rows.map( row => [
				String( row[0] ), Number( row[4] || 0 ), `${ row[1] } · ${ row[2] } · ${ row[3] }`,
			] as const )
		}

		@ $mol_mem
		filtered_rows(): readonly $shm_wordstat_row[] {
			const table = this.table_ready()
			return this.query( `
				SELECT "query", "region", "period", "device", CAST( "date" AS VARCHAR ), "count", "share"
				FROM ${ table }
				WHERE ${ this.where_sql() }
				ORDER BY "date" DESC, "query", "region";
			` ).rows.map( row => ({
				query: String( row[0] || '' ), region: String( row[1] || '' ),
				period: String( row[2] || '' ), device: String( row[3] || '' ),
				date: String( row[4] || '' ).slice( 0, 10 ), count: Number( row[5] || 0 ), share: String( row[6] || '' ),
			}) )
		}

	}

}
